/// PropertiesPanel: Context-aware element property editing panel.
/// Shows BPMNPropertyForm or NodePropertyForm based on active tab's editor type
/// and current selection. Shows diagram metadata when nothing is selected.

import { useCallback, useMemo } from "react";
import { useEditorStore } from "@/stores/editorStore";
import { BPMNPropertyForm } from "./properties/BPMNPropertyForm";
import { NodePropertyForm } from "./properties/NodePropertyForm";
import { PropertyField } from "./properties/PropertyField";
import type { BPMNElementProperties, BPMNElementType } from "@/types/properties";
import type { NodeElementProperties } from "@/types/properties";

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PropertiesPanel() {
  const tabs = useEditorStore((s) => s.tabs);
  const activeTabId = useEditorStore((s) => s.activeTabId);
  const editorStates = useEditorStore((s) => s.editorStates);
  const updateTab = useEditorStore((s) => s.updateTab);

  const activeTab = useMemo(() => tabs.find((t) => t.id === activeTabId), [tabs, activeTabId]);

  const activeEditorState = activeTabId ? editorStates.get(activeTabId) : undefined;
  const selection = activeEditorState?.selection;

  // No active tab
  if (!activeTab) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-zinc-500">
        <p>No editor open</p>
      </div>
    );
  }

  // No selection -- show diagram metadata
  if (!selection || selection.selectedIds.length === 0) {
    return (
      <DiagramMetadataView
        tabId={activeTab.id}
        title={activeTab.title}
        editorType={activeTab.editorType}
        updateTab={updateTab}
      />
    );
  }

  // Multi-selection
  if (selection.selectedIds.length > 1) {
    return (
      <div className="p-3 text-sm text-zinc-400">
        <p className="font-medium">{selection.selectedIds.length} elements selected</p>
        <p className="mt-1 text-xs text-zinc-500">
          Multi-selection property editing not yet supported. Select a single element to edit its
          properties.
        </p>
        {/* Node editor: group selected nodes */}
        {activeTab.editorType === "nodeflow" && (
          <button
            type="button"
            className="mt-3 w-full rounded bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-500 transition-colors"
            onClick={() => {
              window.dispatchEvent(
                new CustomEvent("flowy:groupSelected", { detail: { label: "Group" } }),
              );
            }}
          >
            Group Selected Nodes
          </button>
        )}
      </div>
    );
  }

  // Single selection -- show appropriate form
  if (activeTab.editorType === "bpmn") {
    return <BPMNPropertyView tabId={activeTab.id} selection={selection} />;
  }

  return <NodePropertyView tabId={activeTab.id} selection={selection} />;
}

// ---------------------------------------------------------------------------
// Diagram metadata view (no element selected)
// ---------------------------------------------------------------------------

function DiagramMetadataView({
  tabId,
  title,
  editorType,
  updateTab,
}: {
  tabId: string;
  title: string;
  editorType: string;
  updateTab: (tabId: string, updates: { title?: string }) => void;
}) {
  const handleNameChange = useCallback(
    (value: unknown) => {
      const s = String(value ?? "").trim();
      if (!s) {
        window.alert("Diagram name cannot be empty.");
        return;
      }
      updateTab(tabId, { title: s });
    },
    [tabId, updateTab],
  );

  return (
    <div className="flex flex-col gap-4 p-3">
      <div className="flex flex-col gap-2">
        <div className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">Diagram</div>
        <div className="flex flex-col gap-2 pl-1">
          <PropertyField
            label="Name"
            type="text"
            value={title}
            onChange={handleNameChange}
            placeholder="Diagram name"
          />
          <PropertyField
            label="Type"
            type="text"
            value={editorType === "bpmn" ? "BPMN 2.0 Process" : "Node Flow"}
            onChange={() => undefined}
            disabled
          />
        </div>
      </div>
      <div className="text-xs text-zinc-500 px-1">Select an element to edit its properties.</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// BPMN property view
// ---------------------------------------------------------------------------

function BPMNPropertyView({
  tabId,
  selection,
}: {
  tabId: string;
  selection: {
    selectedIds: string[];
    elementType?: string;
    elementProperties?: Record<string, unknown>;
  };
}) {
  const elementId = selection.selectedIds[0];
  const setSelection = useEditorStore((s) => s.setSelection);

  // Build BPMN properties from selection info
  // Full property fetching will use a query to the editor worker
  const properties: BPMNElementProperties = useMemo(
    () => ({
      id: String(selection.elementProperties?.id ?? elementId),
      type: (selection.elementType as BPMNElementType) ?? "task",
      name: String(selection.elementProperties?.name ?? ""),
      jobType: selection.elementProperties?.jobType as string | undefined,
      retries: selection.elementProperties?.retries as number | undefined,
      jobCustomHeaders: selection.elementProperties?.jobCustomHeaders as
        | Record<string, string>
        | undefined,
      formKey: selection.elementProperties?.formKey as string | undefined,
      assignee: selection.elementProperties?.assignee as string | undefined,
      candidateGroups: selection.elementProperties?.candidateGroups as string | undefined,
      scriptLanguage: selection.elementProperties?.scriptLanguage as string | undefined,
      scriptBody: selection.elementProperties?.scriptBody as string | undefined,
      timerType: selection.elementProperties?.timerType as
        | "duration"
        | "date"
        | "cycle"
        | undefined,
      timerExpression: selection.elementProperties?.timerExpression as string | undefined,
      conditionExpression: selection.elementProperties?.conditionExpression as string | undefined,
      isDefault: selection.elementProperties?.isDefault as boolean | undefined,
      isMultiInstance: selection.elementProperties?.isMultiInstance as boolean | undefined,
      isSequential: selection.elementProperties?.isSequential as boolean | undefined,
      collectionExpression: selection.elementProperties?.collectionExpression as string | undefined,
      elementVariable: selection.elementProperties?.elementVariable as string | undefined,
      inputMappings: selection.elementProperties?.inputMappings as
        | { source: string; target: string }[]
        | undefined,
      outputMappings: selection.elementProperties?.outputMappings as
        | { source: string; target: string }[]
        | undefined,
    }),
    [elementId, selection.elementType, selection.elementProperties],
  );

  const handleChange = useCallback(
    (updated: Partial<BPMNElementProperties>) => {
      // Validate: ID and name cannot be empty
      if (updated.id !== undefined) {
        if (!String(updated.id).trim()) {
          window.alert("ID cannot be empty.");
          return;
        }
      }
      if (updated.name !== undefined) {
        if (!String(updated.name).trim()) {
          window.alert("Name cannot be empty.");
          return;
        }
      }

      const currentProps = selection.elementProperties ?? {};
      const mergedProps = { ...currentProps, ...updated };
      setSelection(tabId, {
        selectedIds: selection.selectedIds,
        elementType: selection.elementType,
        elementProperties: mergedProps,
      });

      const event = new CustomEvent("flowy:propertyChange", {
        detail: {
          elementId,
          editorType: "bpmn",
          properties: updated,
        },
      });
      window.dispatchEvent(event);
    },
    [elementId, tabId, selection, setSelection],
  );

  return <BPMNPropertyForm properties={properties} onChange={handleChange} />;
}

// ---------------------------------------------------------------------------
// Node property view
// ---------------------------------------------------------------------------

function NodePropertyView({
  tabId,
  selection,
}: {
  tabId: string;
  selection: {
    selectedIds: string[];
    elementType?: string;
    elementProperties?: Record<string, unknown>;
  };
}) {
  const elementId = selection.selectedIds[0];
  const setSelection = useEditorStore((s) => s.setSelection);

  // Build Node properties from selection info
  const properties: NodeElementProperties = useMemo(
    () => ({
      id: String(selection.elementProperties?.id ?? elementId),
      nodeType: String(selection.elementProperties?.nodeType ?? selection.elementType ?? "unknown"),
      category: String(selection.elementProperties?.category ?? ""),
      name: String(selection.elementProperties?.name ?? ""),
      inputs: (selection.elementProperties?.inputs as NodeElementProperties["inputs"]) ?? [],
      outputs: (selection.elementProperties?.outputs as NodeElementProperties["outputs"]) ?? [],
      settings: (selection.elementProperties?.settings as Record<string, unknown>) ?? {},
    }),
    [elementId, selection.elementType, selection.elementProperties],
  );

  const handleChange = useCallback(
    (updated: Partial<NodeElementProperties>) => {
      // Validate: name cannot be empty (ID is read-only in Node editor)
      if (updated.name !== undefined) {
        if (!String(updated.name).trim()) {
          window.alert("Name cannot be empty.");
          return;
        }
      }

      // Optimistically update the store so controlled inputs reflect changes immediately
      const currentProps = selection.elementProperties ?? {};
      const mergedProps = { ...currentProps, ...updated };
      setSelection(tabId, {
        selectedIds: selection.selectedIds,
        elementType: selection.elementType,
        elementProperties: mergedProps,
      });

      // Also dispatch to the editor worker for persistence and undo support
      const event = new CustomEvent("flowy:propertyChange", {
        detail: {
          elementId,
          editorType: "nodeflow",
          properties: updated,
        },
      });
      window.dispatchEvent(event);
    },
    [elementId, tabId, selection, setSelection],
  );

  return <NodePropertyForm properties={properties} onChange={handleChange} />;
}
